import React, { useEffect, useState } from "react";
import Tooltip from "../../../Project Page/Tooltip";
import KnowledgeSourceScope from "./KnowledgeSourceScope";
import WritingBriefPanel from "./WritingBriefPanel";

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M6 6l12 12M18 6L6 18"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const HelpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="#9CA3AF" strokeWidth="1.6" />
    <path
      d="M9.6 9.4a2.4 2.4 0 1 1 3.3 2.2c-.7.3-1 .8-1 1.6"
      stroke="#9CA3AF"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <circle cx="12" cy="16.6" r="0.9" fill="#9CA3AF" />
  </svg>
);

// Pick the knowledge scope before generating a section. `onGenerate` receives
// { kb_folder_ids, kb_file_keys, rfp_folder_ids, rfp_file_keys } — all empty
// means "use everything".
const GenerateSectionModal = ({
  isOpen,
  onClose,
  onGenerate,
  orgId,
  folderId,
  isSubsection = false,
  isRegenerate = false,
  generationPrompt = "",
  // Readable writing task from the agentic analysis. When present it is
  // PREVIEWED (read-only) instead of the retrieval query — it is what the
  // writer will actually follow when generating this node.
  writingBrief = "",
  onSavePrompt,
  initialKbScope = null,
  initialRfpScope = null,
}) => {
  const unit = isSubsection ? "Subsection" : "Section";
  const unitLower = isSubsection ? "subsection" : "section";
  const verb = isRegenerate ? "Regenerate" : "Generate";
  const [scope, setScope] = useState({
    kb_folder_ids: [],
    kb_file_keys: [],
    rfp_folder_ids: [],
    rfp_file_keys: [],
  });

  // Editable copy of the section/subsection query. Re-seeded whenever the modal
  // opens or the saved prompt changes (e.g. after a successful save refetches).
  const [promptText, setPromptText] = useState(generationPrompt);
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [promptFocused, setPromptFocused] = useState(false);
  useEffect(() => {
    if (isOpen) setPromptText(generationPrompt);
  }, [isOpen, generationPrompt]);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // When the agentic analysis produced a writing brief, preview THAT (read-only)
  // — it is the actual writing task the generator follows. The retrieval query
  // stays editable only for nodes without a brief (older / ai-path TOCs).
  const hasBrief = !!(writingBrief || "").trim();
  // Prompt editing is available only when the host wires up onSavePrompt.
  const canEditPrompt = !hasBrief && typeof onSavePrompt === "function";
  const promptDirty = promptText.trim() !== (generationPrompt || "").trim();

  const handleSavePrompt = async () => {
    if (!canEditPrompt || !promptDirty || savingPrompt) return;
    setSavingPrompt(true);
    try {
      await onSavePrompt(promptText.trim());
    } finally {
      setSavingPrompt(false);
    }
  };

  // At least one knowledge source must be picked before generating — unless
  // there's nothing selectable anywhere (then fall back to the whole KB).
  const hasSelection =
    scope.kb_folder_ids.length +
      scope.kb_file_keys.length +
      scope.rfp_folder_ids.length +
      scope.rfp_file_keys.length >
    0;
  const noSources = scope.sourcesReady && !scope.hasSources;
  const canGenerate = hasSelection || noSources;

  const handleGenerate = () => {
    if (!canGenerate) return;
    // Selecting the whole KB is equivalent to an empty KB scope (backend searches
    // the entire collection) — send empty so it stamps a "Complete Knowledge
    // Base" scope rather than a "Custom Scope". Project (RFP) scope is left as-is:
    // an empty RFP scope means "no project evidence", so a full selection there
    // must stay explicit.
    onGenerate({
      kb_folder_ids: scope.kbAllSelected ? [] : scope.kb_folder_ids,
      kb_file_keys: scope.kbAllSelected ? [] : scope.kb_file_keys,
      rfp_folder_ids: scope.rfp_folder_ids,
      rfp_file_keys: scope.rfp_file_keys,
      // Distinguishes a whole-KB pick (empty scope, whole KB) from a KB-scoped-out
      // pick (empty scope, no KB) so regenerate can restore the exact selection.
      kb_whole: !!scope.kbAllSelected,
    });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      {/* Capped and column-flexed so a long brief scrolls the body instead of
          pushing the actions off-screen. */}
      <div
        className="w-full max-w-[680px] bg-white rounded-2xl shadow-xl flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-4 shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="font-outfit text-[18px] font-semibold text-[#3F3F46]">
                {verb} Proposal {unit}
              </h2>
              <p className="font-outfit text-[14px] text-[#52525B] mt-0.5">
                {verb} content for this proposal {unitLower} using your selected
                knowledge sources.
              </p>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="-mr-1 -mt-1 text-[#6B7280] hover:text-[#3F3F46] transition-colors"
            >
              <CloseIcon />
            </button>
          </div>
          <div className="mt-4 border-t border-[#D9D9D9]" />
        </div>

        {/* Order: the choice you make (sources) first, the reference material
            (the brief) last — you act on the scope, you only read the brief. */}
        <div className="px-6 py-5 flex-1 min-h-0 overflow-y-auto custom-scrollbar">
          <div className="mb-5">
            <KnowledgeSourceScope
              isOpen={isOpen}
              folderId={folderId}
              orgId={orgId}
              initialKbScope={initialKbScope}
              initialRfpScope={initialRfpScope}
              onChange={setScope}
              selectAllKbWhenEmpty={isRegenerate}
            />
          </div>

          {/* A brief is prose to READ — rendered as a structured, expandable
              panel. A prompt is a field to EDIT — stays a textarea. */}
          {hasBrief ? (
            <div>
              <WritingBriefPanel
                label="Writing Brief"
                text={writingBrief}
                tooltip={`What the writer will convey in this ${unitLower} — produced by the proposal analysis.`}
              />
            </div>
          ) : (
            (canEditPrompt || generationPrompt) && (
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-1.5">
                    <label className="text-[#171717] font-outfit font-medium text-[15px]">
                      Generation Prompt
                    </label>
                    <Tooltip
                      content={`The query used to generate this ${unitLower}'s content. Edit it to steer future ${unitLower} output.`}
                      placement="right"
                    >
                      <HelpIcon />
                    </Tooltip>
                  </div>
                  {canEditPrompt && (
                    <button
                      type="button"
                      onClick={handleSavePrompt}
                      disabled={!promptDirty || savingPrompt}
                      className="px-3 py-1 rounded-md border border-[#8B55DD] text-[#8B55DD] font-outfit text-[13px] font-medium hover:bg-[#8B55DD0A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    >
                      {savingPrompt ? "Saving…" : "Save"}
                    </button>
                  )}
                </div>
                <textarea
                  value={promptText}
                  onChange={(e) => setPromptText(e.target.value)}
                  onFocus={() => setPromptFocused(true)}
                  onBlur={() => setPromptFocused(false)}
                  readOnly={!canEditPrompt}
                  rows={5}
                  placeholder={`Describe what to retrieve for this ${unitLower}…`}
                  style={{
                    backgroundColor:
                      canEditPrompt && promptFocused ? "#FFFFFF" : "#FAFAFA",
                  }}
                  className={`w-full p-3 border border-gray-200 rounded-lg resize-none font-outfit text-[14px] text-[#52525B] placeholder:text-gray-400 placeholder:font-normal ${
                    canEditPrompt
                      ? "focus:outline-none focus:ring-2 focus:ring-[#8B55DD] focus:border-transparent"
                      : "focus:outline-none cursor-default"
                  }`}
                />
              </div>
            )
          )}
        </div>

        <div className="px-6 pt-3 pb-4 shrink-0 border-t border-[#F1F1F4] flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 rounded-lg border border-[#8B55DD] text-[#8B55DD] font-outfit text-[14px] font-medium hover:bg-pink-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={!canGenerate}
            className="px-4 py-2 rounded-lg bg-[#8B55DD] text-white font-outfit text-[14px] font-medium hover:bg-[#6D28D9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#8B55DD]"
          >
            {verb} {unit}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenerateSectionModal;
